import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useUserStore from '../store/userStore';
import BrandMark from './BrandMark';
import { ROUTES } from '../routes/routeConfig';
import './GlobalNav.css';

const primaryNav = [
  { label: '首页', path: ROUTES.home, match: (pathname) => pathname === ROUTES.home },
  { label: '财富学院', path: ROUTES.academy, match: (pathname) => pathname.startsWith(ROUTES.academy) || pathname.startsWith(ROUTES.legacyDocs) },
  { label: '市场研究院', path: ROUTES.market, match: (pathname) => pathname.startsWith(ROUTES.market) || isLegacyMarketPath(pathname) },
  { label: '交易所注册', path: ROUTES.rebate, match: (pathname) => pathname.startsWith(ROUTES.rebate) },
  { label: '社群', path: ROUTES.community, match: (pathname) => pathname.startsWith(ROUTES.community) },
];

function isLegacyMarketPath(pathname) {
  return [
    ROUTES.legacyMarketHeatmap,
    ROUTES.legacyPatternMatch,
    ROUTES.legacyDumpDetective,
    ROUTES.legacySupportResistance,
    ROUTES.legacyInstitutions,
    ROUTES.legacyFundamentals,
  ].some((path) => pathname.startsWith(path));
}

function GlobalNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn, userInfo, logout } = useUserStore();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = location.pathname;

  const goHome = () => {
    if (pathname === ROUTES.home) {
      document.getElementById('hero')?.scrollIntoView({ behavior: 'smooth' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate(ROUTES.home);
    }
  };

  const handleNavigate = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleLogout = () => {
    logout();
    setMenuOpen(false);
    setMobileOpen(false);
    navigate(ROUTES.home);
  };

  const displayUsername = userInfo.username && userInfo.username.length > 12
    ? `${userInfo.username.substring(0, 10)}...`
    : userInfo.username;

  return (
    <nav className="gn">
      <div className="gn-inner">
        <button className="gn-logo" onClick={goHome}>
          GHOST
          <BrandMark className="gn-logo-mark" />
        </button>

        <div className="gn-links">
          {primaryNav.map((item) => (
            <button
              key={item.path}
              className={`gn-link${item.match(pathname) ? ' active' : ''}`}
              onClick={() => handleNavigate(item.path)}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="gn-actions">
          {isLoggedIn ? (
            <div className="gn-user-wrap">
              <button
                className="gn-user-btn"
                onClick={() => setMenuOpen(!menuOpen)}
                onBlur={() => setTimeout(() => setMenuOpen(false), 150)}
              >
                <svg width="18" height="18" fill="none" viewBox="0 0 24 24">
                  <circle cx="12" cy="8" r="4" fill="var(--accent-secondary)" opacity="0.6" />
                  <path d="M4 20c0-4 4-6 8-6s8 2 8 6" stroke="var(--accent-secondary)" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <span>{displayUsername}</span>
                <svg width="10" height="10" fill="none" viewBox="0 0 12 12">
                  <path d={menuOpen ? 'M3 8L6 5L9 8' : 'M3 5L6 8L9 5'} stroke="var(--accent-secondary)" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
              {menuOpen && (
                <div className="gn-user-menu">
                  <div className="gn-user-menu-item" onClick={() => { navigate(ROUTES.profile); setMenuOpen(false); }}>
                    个人资料
                  </div>
                  <div className="gn-user-menu-item gn-user-menu-logout" onClick={handleLogout}>
                    退出登录
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              <button className="gn-btn-outline" onClick={() => navigate(ROUTES.login)}>登录</button>
              <button className="gn-btn-primary" onClick={() => navigate(ROUTES.register)}>注册</button>
            </>
          )}
          <button
            className="gn-hamburger"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? '关闭菜单' : '打开菜单'}
            aria-expanded={mobileOpen}
            aria-controls="gn-mobile-drawer"
          >
            <svg width="22" height="22" fill="none" viewBox="0 0 24 24">
              {mobileOpen ? (
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              ) : (
                <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              )}
            </svg>
          </button>
        </div>
      </div>

      <div className={`gn-drawer-backdrop${mobileOpen ? ' open' : ''}`} onClick={() => setMobileOpen(false)} />
      <div
        id="gn-mobile-drawer"
        className={`gn-drawer${mobileOpen ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!mobileOpen}
        aria-label="移动端导航菜单"
      >
        <div className="gn-drawer-header">
          <div className="gn-drawer-brand-wrap">
            <span className="gn-drawer-brand">GHOST</span>
            <span className="gn-drawer-subtitle">Web3 Community</span>
          </div>
          <button className="gn-drawer-close" onClick={() => setMobileOpen(false)} aria-label="关闭菜单">
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <div className="gn-drawer-body">
          <div className="gn-drawer-section-label">主导航</div>
          {primaryNav.map((item) => (
            <button
              key={item.path}
              className={`gn-drawer-item${item.match(pathname) ? ' active' : ''}`}
              onClick={() => handleNavigate(item.path)}
            >
              {item.label}
            </button>
          ))}

          <div className="gn-drawer-divider" />
          {isLoggedIn ? (
            <>
              <button className="gn-drawer-item" onClick={() => handleNavigate(ROUTES.profile)}>个人资料</button>
              <button className="gn-drawer-item gn-drawer-logout" onClick={handleLogout}>退出登录</button>
            </>
          ) : (
            <div className="gn-drawer-auth">
              <button className="gn-drawer-auth-login" onClick={() => handleNavigate(ROUTES.login)}>登录</button>
              <button className="gn-drawer-auth-register" onClick={() => handleNavigate(ROUTES.register)}>注册</button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}

export default GlobalNav;
